import { SelectableCard } from '@components/ui/selectable-card';
import { TextHeader } from '@components/ui/text-header';
import { useDialog } from '@context/dialog';
import { CollectionTypeEnum, Organization } from '@givz/core-client';
import AddSharp from '@mui/icons-material/AddSharp';
import { Button, Stack, Typography } from '@mui/material';
import { useCuratedCollection } from '@queries/collections';
import { FunctionComponent } from 'react';
import { FeaturedCharitiesDialog } from './featured-charities-dialog';

export type FeaturedCharitiesProps = {
  type: CollectionTypeEnum;
  onChangeSelectedCharities: (
    selectedCharities: Map<string, Organization>,
  ) => void;
  onChangeType: (type: CollectionTypeEnum) => void;
  selectedCharities: Map<string, Organization>;
};

export const FeaturedCharities: FunctionComponent<FeaturedCharitiesProps> = (
  props,
) => {
  const { onChangeSelectedCharities, onChangeType, selectedCharities, type } =
    props;
  const { closeDialog, prepareDialog, showDialog } = useDialog();
  const curatedCollectionQuery = useCuratedCollection();
  const curatedCollection = curatedCollectionQuery.data?.data;

  const onCurated = () => {
    const curatedCharities = new Map(
      curatedCollection?.organizations.map((organization) => [
        organization.gid,
        organization,
      ]),
    );
    onChangeSelectedCharities(curatedCharities);
    onChangeType(CollectionTypeEnum.Curated);
  };

  const onSelectCurated = () => {
    if (type !== CollectionTypeEnum.Curated) {
      onCurated();
    }
  };

  const onSelectHandpicked = () => {
    if (type !== CollectionTypeEnum.Handpicked) {
      onChangeSelectedCharities(new Map());
      onChangeType(CollectionTypeEnum.Handpicked);
    }
  };

  const onToggleHandpickedCharity = (params: {
    id: string;
    data?: Organization;
  }) => {
    const { data, id } = params;
    if (selectedCharities.has(id)) {
      selectedCharities.delete(id);
    } else {
      selectedCharities.set(id, data!);
    }

    onChangeSelectedCharities(new Map(selectedCharities.entries()));
  };

  const onDoneDialog = (selected: Map<string, Organization>) => {
    onChangeSelectedCharities(new Map(selected.entries()));
    closeDialog();
  };

  const onAddCharity = () => {
    prepareDialog({
      content: (
        <FeaturedCharitiesDialog
          onDone={onDoneDialog}
          selectedItems={selectedCharities}
        />
      ),
      dialogProps: { fullWidth: true, maxWidth: 'md' },
    });
    showDialog();
  };

  const isCurated = type === CollectionTypeEnum.Curated;
  const isHandpicked = type === CollectionTypeEnum.Handpicked;

  let title = 'Givz Curated';
  let subtitle = 'Givz selects new charities to feature each calendar month';

  if (isHandpicked) {
    title = 'Handpicked Charities';
    subtitle = 'You must select at least one, and no more than ten charities';
  }

  return (
    <Stack direction="column" spacing={1.5}>
      <Typography variant="h4" display="block" gutterBottom>
        Featured Charities
      </Typography>
      <Stack direction={{ md: 'row', xs: 'column' }} spacing={1}>
        <SelectableCard
          title="Givz Curated"
          subtitle="Feature charities curated monthly by Givz"
          radioProps={{ checked: isCurated }}
          onClick={onSelectCurated}
        />
        <SelectableCard
          title="Handpicked Charities"
          subtitle="Pick your own charities to feature"
          radioProps={{ checked: isHandpicked }}
          onClick={onSelectHandpicked}
        />
      </Stack>
      <Stack direction="row" justifyContent="space-between">
        <TextHeader
          title={title}
          subtitle={subtitle}
          subtitleProps={{ display: 'block', variant: 'body2' }}
          titleProps={{ display: 'block', fontWeight: 700, variant: 'body1' }}
        />
        {isHandpicked && (
          <Button
            onClick={onAddCharity}
            variant="outlined"
            startIcon={<AddSharp />}
          >
            Add Charity
          </Button>
        )}
      </Stack>
      {isHandpicked && (
        <Stack direction="column" spacing={1}>
          {Array.from(selectedCharities.entries() ?? []).map(([id, data]) => {
            return (
              <SelectableCard
                key={id}
                title={data.name}
                subtitle={data.ein}
                avatarProps={{ src: data.logoUrl }}
                radioProps={{
                  checked: isHandpicked,
                  onClick: () => onToggleHandpickedCharity({ data, id }),
                }}
              />
            );
          })}
        </Stack>
      )}
      {!isHandpicked && (
        <Stack direction="column" spacing={1}>
          {curatedCollection?.organizations.map((data) => {
            return (
              <SelectableCard
                key={data.ein}
                title={data.name}
                subtitle={data.ein}
                avatarProps={{ src: data.logoUrl }}
              />
            );
          })}
        </Stack>
      )}
    </Stack>
  );
};
